import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';
import { BaseAdministrationService } from '../../modules/administration/_services/base-administration.service';
import { baseUserActions, basePspBranchActions, baseSharedActions } from '../actions';
import { BaseCoreState } from '../reducers';
import { baseAuthSelectors, basePspBranchSelectors } from '../selectors';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BasePspBranchService } from '../../modules/psp-branch/base-psp-branch.service';

@Injectable()
export class BasePspBranchEffects {
  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private pspBranchService: BasePspBranchService,
    private administrationService: BaseAdministrationService
  ) {}

  loadPspBranchList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.loadPspBranchList),
      withLatestFrom(this.store.select(basePspBranchSelectors.getBranchInactiveFilter)),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([actionAndFilter, userInfo]) => {
        const [action, active] = actionAndFilter;
        return this.pspBranchService.getPspBranches(userInfo.organizationId, active ? active : undefined).pipe(
          mergeMap((response) => {
            return [
              basePspBranchActions.setPspBranchList({ payload: response }),
              basePspBranchActions.successPspBranchList(),
            ];
          }),
          catchError(() => {
            return of(basePspBranchActions.errorPspBranchList());
          })
        );
      })
    );
  });

  setBranchStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.setPspBranchActiveCrud),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = action.payload.setActive
          ? this.pspBranchService.setActiveBranchStatus(user.organizationId, action.payload.branchId)
          : this.pspBranchService.setInactiveBranchStatus(user.organizationId, action.payload.branchId);
        return source$.pipe(
          mergeMap(() => {
            return [basePspBranchActions.loadPspBranchList(), basePspBranchActions.successPspBranchCrud()];
          }),
          catchError(() => {
            return of(basePspBranchActions.errorPspBranchCrud());
          })
        );
      })
    );
  });

  createPspBranch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.createPspBranchCrud),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, userInfo]) => {
        return this.pspBranchService.createPspBranch(userInfo.organizationId, action.payload.body).pipe(
          mergeMap(() => [
            basePspBranchActions.loadPspBranchList(),
            basePspBranchActions.successPspBranchCrud(),
            baseSharedActions.addSystemAlert({
              payload: {
                class: BaseToastAlertClasses.success,
                body: 'branch.createdSuccessfully',
              },
            }),
          ]),
          catchError(() => {
            return of(basePspBranchActions.errorPspBranchCrud());
          })
        );
      })
    );
  });

  addBranchUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.addBranchUsers),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, userInfo]) => {
        return this.pspBranchService
          .addUsersToBranch(action.payload.branch, userInfo.organizationId, action.payload.users)
          .pipe(
            mergeMap(() => [
              baseSharedActions.addSystemAlert({
                payload: {
                  class: BaseToastAlertClasses.success,
                  body: 'branch.updatedSuccessfully',
                },
              }),
              baseSharedActions.loadBranchUsers({
                payload: { branch: action.payload.branch },
              }),
              baseUserActions.loadUserList({ payload: {} }),
              basePspBranchActions.loadPspBranchList(),
            ])
          );
      })
    );
  });

  deleteBranchUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.deleteBranchUser),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, userInfo]) => {
        return this.pspBranchService
          .deleteUserFromBranch(userInfo.organizationId, action.payload.user, action.payload.branch)
          .pipe(
            mergeMap(() => [
              baseSharedActions.addSystemAlert({
                payload: {
                  class: BaseToastAlertClasses.success,
                  body: 'branch.userDeletedSuccessfully',
                },
              }),
              baseSharedActions.loadBranchUsers({
                payload: { branch: action.payload.branch },
              }),
              baseUserActions.loadUserList({ payload: {} }),
              basePspBranchActions.loadPspBranchList(),
            ])
          );
      })
    );
  });

  updateUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.updateBranchUsers),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, userInfo]) => {
        return this.pspBranchService
          .updateUsersInBranch(action.payload.branch, userInfo.organizationId, action.payload.users)
          .pipe(
            mergeMap(() => [
              baseSharedActions.addSystemAlert({
                payload: {
                  class: BaseToastAlertClasses.success,
                  body: 'branch.updatedSuccessfully',
                },
              }),
              baseSharedActions.loadBranchUsers({
                payload: { branch: action.payload.branch },
              }),
              baseUserActions.loadUserList({ payload: {} }),
              basePspBranchActions.loadPspBranchList(),
            ])
          );
      })
    );
  });

  loadCurrentBranch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.loadBranch),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) =>
        this.pspBranchService.getPspBranch(user.organizationId, action.payload.branch).pipe(
          mergeMap((response) => [
            basePspBranchActions.setBranch({ payload: response }),
            basePspBranchActions.successBranch(),
          ]),
          catchError(() => of(basePspBranchActions.errorBranch()))
        )
      )
    );
  });

  updateCurrentBranch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.updateBranch),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, userInfo]) =>
        this.administrationService
          .updatePspBranch(action.payload.branch, userInfo.organizationId, action.payload.body)
          .pipe(
            mergeMap(() => [
              basePspBranchActions.setBranch({ payload: action.payload.body }),
              basePspBranchActions.loadPspBranchList(),
              basePspBranchActions.successBranch(),
              baseSharedActions.addSystemAlert({
                payload: {
                  class: BaseToastAlertClasses.success,
                  body: 'branch.updatedSuccessfully',
                },
              }),
            ]),
            catchError(() => of(basePspBranchActions.errorBranch()))
          )
      )
    );
  });

  sendIzsRequestInformation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePspBranchActions.sendIzsRequestInformation),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) =>
        this.pspBranchService.sendIzsRequestInformation(user.organizationId, action.payload).pipe(
          mergeMap(() => [
            basePspBranchActions.successSendIzsRequestInformation(),
            basePspBranchActions.loadPspBranchList(),
            basePspBranchActions.loadBranch({
              payload: {
                branch: action.payload,
              },
            }),
            baseSharedActions.addSystemAlert({
              payload: {
                class: BaseToastAlertClasses.success,
                body: 'izs.pspBranchTab.requestWasSentAlert',
              },
            }),
          ]),
          catchError(() => [
            basePspBranchActions.errorSendIzsRequestInformation(),
            baseSharedActions.addSystemAlert({
              payload: {
                class: BaseToastAlertClasses.error,
                body: 'izs.pspBranchTab.requestFailedAlert',
              },
            }),
          ])
        )
      )
    );
  });
}
